/**
 * ProShip Direct v2 — Courier Setup, every dimension on one page.
 *
 * WHY ONE PAGE. Setting up an account means supplying coverage, then zones, then TAT, then a rate
 * card — for the SAME account, in one sitting. As five routes that was five
 * navigations, and each one dropped the merchant back on the first account in the list, so the
 * account had to be re-picked at every step. Tabs keep the account and move the subject, which is
 * the way round the work actually happens.
 *
 * THE URL CARRIES BOTH: /dp/courier-setup?account=own-2&tab=/zones. Both halves are addressable,
 * so My Couriers can link to the exact cell a merchant clicked, Home can link to the outstanding
 * dimension, and a merchant can send someone a link to the thing that needs doing. The tab id is
 * the dimension's old standalone route, which means the original standalone paths still resolve and land
 * on their tab rather than 404ing — no bookmark breaks.
 *
 * NOTHING IS DUPLICATED HERE. Each tab renders the page component that already existed, in its
 * embedded form: SetupPageShell drops its own container and title, this page provides them once.
 * Copying each body into a switch would be the debt the standards call out (§4.2), and they
 * would drift the first time one of them changed.
 */
import React from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import { Box, Tabs, Tab, Tooltip } from '@mui/material';

import withLayout from '../../../hocs/Layout';
import PageTitle from '../../../common/PageTitle';
import { SETUP_DIMENSIONS } from '../data/defaults';
import { useOwnCouriers, usePlan } from '../data/hooks';
import { pageContainerSx } from '../../../styles/patterns';
import { SUBTLE, NAVY, BORDER, WARNING, SUCCESS, PRIMARY_BLUE } from '../../../styles/foundation/colors';
import { FONT, SIZE, WEIGHT } from '../../../styles/foundation/typography';
import { GAP } from '../../../styles/foundation/spacing';

import { Serviceability } from './Serviceability';
import { Zones } from './Zones';
import { Tat } from './Tat';
import { RateCard } from './RateCard';

/** One tab body per dimension key. Components, not copies — see the note above. */
const PANELS = {
  serviceability: Serviceability,
  zones: Zones,
  tat: Tat,
  rateCard: RateCard,
};

/**
 * A dot on the tab saying whether this dimension is the merchant's own.
 *
 * Without it a merchant has to open all five to find the one still on a ProShip default — which is
 * the single question this page exists to answer. Required-and-missing is amber rather than grey:
 * a rate card that was never uploaded is not "running on ours", there is nothing to run on.
 */
function TabMark({ state }) {
  const tone = { OWN: SUCCESS, MISSING: WARNING, DEFAULT: BORDER }[state];
  const title = {
    OWN: 'Direct',
    MISSING: 'Not uploaded — nothing to fall back on',
    DEFAULT: 'Running on the ProShip default',
  }[state];

  return (
    <Tooltip title={title}>
      <Box
        component='span'
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: tone,
          flexShrink: 0,
          ...(state === 'DEFAULT' ? { border: `1px solid ${SUBTLE}55`, bgcolor: 'transparent' } : {}),
        }}
      />
    </Tooltip>
  );
}

export function CourierSetup() {
  const [params, setParams] = useSearchParams();
  const { pathname } = useLocation();
  const { couriers } = useOwnCouriers();
  const { plan } = usePlan();

  /*
    ?tab= first, then the path. The path is what makes the five old routes work: /dp/zones renders
    this page, finds no ?tab=, and recognises its own pathname as the Zones tab. Accepting the bare
    key too means ?tab=zones reads as well as ?tab=/zones, since one is the obvious thing to type.
  */
  const requestedTab = params.get('tab');
  const dimension =
    SETUP_DIMENSIONS.find((d) => d.route === requestedTab || d.key === requestedTab) ||
    SETUP_DIMENSIONS.find((d) => pathname.endsWith(d.route)) ||
    SETUP_DIMENSIONS[0];

  /*
    Only resolved while the plan is active. The tabs render either way — the shell inside them
    explains that the plan is off — but the dots on them read a Direct account, and showing that
    account's setup state on a plan nobody has bought would be the one place Direct leaks through.
  */
  const account = plan.active
    ? couriers.find((c) => c.id === params.get('account')) || couriers[0]
    : null;

  const choose = (route) => {
    const next = new URLSearchParams(params);
    next.set('tab', route);
    // The account rides along untouched: changing the subject must not change the account.
    setParams(next);
  };

  const stateOf = (d) => {
    if (account?.config?.[d.key] === 'OWN') return 'OWN';
    return d.required ? 'MISSING' : 'DEFAULT';
  };

  const Panel = PANELS[dimension.key];

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle={dimension.blurb}>Courier Setup</PageTitle>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: GAP.lg }}>
        <Box sx={{ borderBottom: `1px solid ${BORDER}` }}>
          <Tabs
            value={dimension.route}
            onChange={(e, next) => choose(next)}
            variant='scrollable'
            scrollButtons='auto'
            sx={{
              minHeight: 38,
              '& .MuiTab-root': {
                fontFamily: FONT,
                fontSize: SIZE['2xs'],
                fontWeight: WEIGHT.semibold,
                textTransform: 'none',
                minHeight: 38,
                py: 0,
                color: SUBTLE,
                '&.Mui-selected': { color: PRIMARY_BLUE },
              },
            }}
          >
            {SETUP_DIMENSIONS.map((d) => (
              <Tab
                key={d.key}
                value={d.route}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    {/* Only meaningful once an account is resolved — no dot beats a wrong dot. */}
                    {account && <TabMark state={stateOf(d)} />}
                    <Box component='span' sx={{ color: 'inherit' }}>
                      {d.label}
                    </Box>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Box>

        {/*
          Keyed on the dimension so a tab switch mounts a fresh body. These panels hold pending
          uploads and half-reviewed files in local state; carrying one tab's staged file over into
          another dimension would be worse than losing it.
        */}
        <Box sx={{ color: NAVY }}>
          <Panel key={dimension.key} embedded />
        </Box>
      </Box>
    </Box>
  );
}

export default withLayout(CourierSetup);
